"use client";

import React, {
  useState, useRef, useEffect, useCallback,
  useMemo, memo, WheelEvent as RWheelEvent,
} from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

// ─── Fallback ──────────────────────────────────────────────────────────────────
const FALLBACK =
  "https://images.unsplash.com/photo-1617137968427-85924c800a22?w=600&q=80";

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

// ══════════════════════════════════════════════════════════════════════════════
// useZoom
// ══════════════════════════════════════════════════════════════════════════════
interface ZoomState { active: boolean; x: number; y: number }

function useZoom() {
  const [zoom, setZoom] = useState<ZoomState>({ active: false, x: 0.5, y: 0.5 });

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setZoom({
      active: true,
      x: clamp((e.clientX - rect.left) / rect.width, 0, 1),
      y: clamp((e.clientY - rect.top) / rect.height, 0, 1),
    });
  }, []);

  const onMouseLeave = useCallback(() => {
    setZoom((z) => ({ ...z, active: false }));
  }, []);

  return { zoom, onMouseMove, onMouseLeave };
}

// ══════════════════════════════════════════════════════════════════════════════
// useSwipe
// ══════════════════════════════════════════════════════════════════════════════
interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove:  (e: React.TouchEvent) => void;
  onTouchEnd:   () => void;
  swipeDelta:   number;
  swiping:      boolean;
}

function useSwipe(onSwipeLeft: () => void, onSwipeRight: () => void): SwipeHandlers {
  const startX = useRef(0);
  const [swipeDelta, setSwipeDelta] = useState(0);
  const [swiping,    setSwiping]    = useState(false);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    startX.current = e.touches[0].clientX;
    setSwiping(true);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!swiping || e.touches.length !== 1) return;
    setSwipeDelta(e.touches[0].clientX - startX.current);
  }, [swiping]);

  const onTouchEnd = useCallback(() => {
    setSwiping(false);
    if (swipeDelta < -50) onSwipeLeft();
    else if (swipeDelta > 50) onSwipeRight();
    setSwipeDelta(0);
  }, [swipeDelta, onSwipeLeft, onSwipeRight]);

  return { onTouchStart, onTouchMove, onTouchEnd, swipeDelta, swiping };
}

// ══════════════════════════════════════════════════════════════════════════════
// usePinchZoom
// ══════════════════════════════════════════════════════════════════════════════
interface PinchZoomState { scale: number; translateX: number; translateY: number }

function usePinchZoom(containerRef: React.RefObject<HTMLElement | null>) {
  const [state, setState] = useState<PinchZoomState>({ scale: 1, translateX: 0, translateY: 0 });
  const lastTap    = useRef(0);
  const lastDist   = useRef(0);
  const lastCenter = useRef({ x: 0, y: 0 });
  const dragStart  = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const isPinching = useRef(false);

  const reset = useCallback(() => {
    setState({ scale: 1, translateX: 0, translateY: 0 });
    isPinching.current = false;
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      isPinching.current = true;
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      lastDist.current = Math.hypot(dx, dy);
      lastCenter.current = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        setState((s) =>
          s.scale > 1
            ? { scale: 1, translateX: 0, translateY: 0 }
            : { scale: 2.5, translateX: 0, translateY: 0 }
        );
      }
      lastTap.current = now;
      setState((s) => {
        dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx: s.translateX, ty: s.translateY };
        return s;
      });
    }
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx   = e.touches[1].clientX - e.touches[0].clientX;
      const dy   = e.touches[1].clientY - e.touches[0].clientY;
      const dist = Math.hypot(dx, dy);
      const scaleDelta = dist / lastDist.current;
      lastDist.current = dist;
      setState((s) => ({ ...s, scale: clamp(s.scale * scaleDelta, 1, 5) }));
    } else if (e.touches.length === 1 && dragStart.current) {
      setState((s) => {
        if (s.scale <= 1) return s;
        e.preventDefault();
        const deltaX   = e.touches[0].clientX - dragStart.current!.x;
        const deltaY   = e.touches[0].clientY - dragStart.current!.y;
        const container = containerRef.current;
        const maxTx    = container ? (container.clientWidth  * (s.scale - 1)) / 2 : 200;
        const maxTy    = container ? (container.clientHeight * (s.scale - 1)) / 2 : 200;
        return {
          ...s,
          translateX: clamp(dragStart.current!.tx + deltaX, -maxTx, maxTx),
          translateY: clamp(dragStart.current!.ty + deltaY, -maxTy, maxTy),
        };
      });
    }
  }, [containerRef]);

  const onTouchEnd = useCallback(() => {
    isPinching.current = false;
    dragStart.current  = null;
    setState((s) => (s.scale < 1.05 ? { scale: 1, translateX: 0, translateY: 0 } : s));
  }, []);

  return { state, reset, onTouchStart, onTouchMove, onTouchEnd };
}

// ══════════════════════════════════════════════════════════════════════════════
// VerticalThumbnails — left column, exactly like the screenshot
// ══════════════════════════════════════════════════════════════════════════════
interface VerticalThumbnailsProps {
  images:      string[];
  activeIndex: number;
  onSelect:    (i: number) => void;
}

const VerticalThumbnails = memo(function VerticalThumbnails({
  images, activeIndex, onSelect,
}: VerticalThumbnailsProps) {
  const listRef    = useRef<HTMLDivElement>(null);
  const activeRef  = useRef<HTMLButtonElement>(null);

  // Scroll active thumb into view
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIndex]);

  return (
    <div
      ref={listRef}
      className="flex flex-col gap-2 overflow-y-auto hide-scrollbar"
      style={{ maxHeight: "100%" }}
      role="listbox"
      aria-label="Product image thumbnails"
    >
      {images.map((src, i) => (
        <button
          key={i}
          ref={i === activeIndex ? activeRef : undefined}
          role="option"
          aria-selected={i === activeIndex}
          onClick={() => onSelect(i)}
          className="flex-shrink-0 focus:outline-none"
          style={{ width: 72, height: 86 }}
        >
          <div
            className={`w-full h-full rounded-md overflow-hidden border-2 transition-all duration-200 ${
              i === activeIndex
                ? "border-[#1a1a1a] shadow-md scale-[1.04]"
                : "border-[#e0dbd2] hover:border-[#b8955a] hover:scale-[1.02]"
            }`}
          >
            <img
              loading="lazy"
              src={src}
              alt={`View ${i + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
            />
          </div>
        </button>
      ))}
    </div>
  );
});

// ══════════════════════════════════════════════════════════════════════════════
// FullscreenViewer
// ══════════════════════════════════════════════════════════════════════════════
interface FullscreenViewerProps {
  images:      string[];
  startIndex:  number;
  productName: string;
  onClose:     () => void;
}

const FullscreenViewer = memo(function FullscreenViewer({
  images, startIndex, productName, onClose,
}: FullscreenViewerProps) {
  const [current,    setCurrent]    = useState(startIndex);
  const [viewerZoom, setViewerZoom] = useState(1);
  const [dragPos,    setDragPos]    = useState({ x: 0, y: 0 });
  const [dragging,   setDragging]   = useState(false);
  const [fade,       setFade]       = useState(true);
  const dragOrigin = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);
  const imgRef     = useRef<HTMLDivElement>(null);

  const navigate = useCallback((dir: 1 | -1) => {
    setFade(false);
    setTimeout(() => {
      setCurrent((c) => (c + dir + images.length) % images.length);
      setViewerZoom(1);
      setDragPos({ x: 0, y: 0 });
      setFade(true);
    }, 120);
  }, [images.length]);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === "Escape")      onClose();
      if (e.key === "ArrowLeft")   navigate(-1);
      if (e.key === "ArrowRight")  navigate(1);
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [navigate, onClose]);

  useEffect(() => {
    const preload = (idx: number) => { const img = new Image(); img.src = images[(idx + images.length) % images.length]; };
    preload(current + 1); preload(current - 1);
  }, [current, images]);

  const onWheel = useCallback((e: RWheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    setViewerZoom((z) => clamp(z - e.deltaY * 0.001, 1, 5));
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (viewerZoom <= 1) return;
    setDragging(true);
    dragOrigin.current = { mx: e.clientX, my: e.clientY, ox: dragPos.x, oy: dragPos.y };
  }, [viewerZoom, dragPos]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging || !dragOrigin.current) return;
    const dx = e.clientX - dragOrigin.current.mx;
    const dy = e.clientY - dragOrigin.current.my;
    const container = imgRef.current;
    const maxTx = container ? (container.clientWidth  * (viewerZoom - 1)) / 2 : 300;
    const maxTy = container ? (container.clientHeight * (viewerZoom - 1)) / 2 : 300;
    setDragPos({ x: clamp(dragOrigin.current.ox + dx, -maxTx, maxTx), y: clamp(dragOrigin.current.oy + dy, -maxTy, maxTy) });
  }, [dragging, viewerZoom]);

  const onMouseUp = useCallback(() => { setDragging(false); dragOrigin.current = null; }, []);

  const pinch = usePinchZoom(imgRef);

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/95 flex flex-col"
      role="dialog" aria-modal="true"
      aria-label={`Image viewer for ${productName}`}
      style={{ animation: "fsOpen 0.22s ease-out forwards" }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
        <span className="font-['DM_Mono'] text-[12px] tracking-[0.2em] uppercase text-white/50">
          {current + 1} / {images.length}
        </span>
        <div className="flex items-center gap-3">
          <button onClick={() => setViewerZoom((z) => clamp(z - 0.5, 1, 5))} aria-label="Zoom out"
            className="p-2 rounded-full border border-white/20 text-white/70 hover:border-white/60 hover:text-white transition-colors">
            <ZoomOut size={14} />
          </button>
          <span className="font-['DM_Mono'] text-[11px] text-white/40 w-8 text-center">{Math.round(viewerZoom * 100)}%</span>
          <button onClick={() => setViewerZoom((z) => clamp(z + 0.5, 1, 5))} aria-label="Zoom in"
            className="p-2 rounded-full border border-white/20 text-white/70 hover:border-white/60 hover:text-white transition-colors">
            <ZoomIn size={14} />
          </button>
          <button onClick={onClose} aria-label="Close viewer"
            className="ml-1 p-2 rounded-full border border-white/20 text-white/70 hover:border-white hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div
        ref={imgRef}
        className="flex-1 overflow-hidden flex items-center justify-center relative select-none"
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
        onWheel={onWheel}
        onTouchStart={pinch.onTouchStart} onTouchMove={pinch.onTouchMove} onTouchEnd={pinch.onTouchEnd}
        style={{ cursor: viewerZoom > 1 ? (dragging ? "grabbing" : "grab") : "default" }}
      >
        <img
          src={images[current]}
          alt={`${productName} — view ${current + 1}`}
          draggable={false}
          onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
          className="max-w-full max-h-full object-contain pointer-events-none"
          style={{
            opacity: fade ? 1 : 0,
            transform: `scale(${viewerZoom * pinch.state.scale}) translate(${dragPos.x / viewerZoom + pinch.state.translateX}px, ${dragPos.y / viewerZoom + pinch.state.translateY}px)`,
            transition: dragging ? "none" : "opacity 0.14s ease, transform 0.18s ease",
            willChange: "transform",
          }}
        />
        {images.length > 1 && (
          <>
            <button onClick={() => navigate(-1)} aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white/25 transition-colors">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => navigate(1)} aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white/25 transition-colors">
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="shrink-0 px-4 py-3 border-t border-white/10">
          <div className="flex gap-2 justify-center overflow-x-auto hide-scrollbar">
            {images.map((src, i) => (
              <button key={i}
                onClick={() => { setCurrent(i); setViewerZoom(1); setDragPos({ x: 0, y: 0 }); }}
                aria-label={`View image ${i + 1}`}
                className={`flex-shrink-0 w-12 h-14 rounded border-2 overflow-hidden transition-all duration-200 ${
                  current === i ? "border-[#b8955a] scale-110" : "border-white/20 hover:border-white/50"
                }`}>
                <img loading="lazy" src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

// ══════════════════════════════════════════════════════════════════════════════
// MobileGallery – swipe + pinch for mobile (<md)
// ══════════════════════════════════════════════════════════════════════════════
interface MobileGalleryProps {
  images:           string[];
  activeIndex:      number;
  onIndexChange:    (i: number) => void;
  onOpenFullscreen: (i: number) => void;
  productName:      string;
}

const MobileGallery = memo(function MobileGallery({
  images, activeIndex, onIndexChange, onOpenFullscreen, productName,
}: MobileGalleryProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pinch = usePinchZoom(containerRef as React.RefObject<HTMLElement>);

  const goNext = useCallback(() => { pinch.reset(); onIndexChange(Math.min(activeIndex + 1, images.length - 1)); }, [activeIndex, images.length, onIndexChange, pinch]);
  const goPrev = useCallback(() => { pinch.reset(); onIndexChange(Math.max(activeIndex - 1, 0)); },               [activeIndex, onIndexChange, pinch]);
  const swipe  = useSwipe(goNext, goPrev);

  useEffect(() => { pinch.reset(); }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="w-full aspect-[4/5] overflow-hidden bg-[#f7f4ef] rounded-xl relative cursor-pointer"
        onTouchStart={(e) => { swipe.onTouchStart(e); pinch.onTouchStart(e); }}
        onTouchMove={(e)  => { swipe.onTouchMove(e);  pinch.onTouchMove(e);  }}
        onTouchEnd={()    => { swipe.onTouchEnd();     pinch.onTouchEnd();    }}
        onClick={() => onOpenFullscreen(activeIndex)}
      >
        <img
          src={images[activeIndex]}
          alt={`${productName} — view ${activeIndex + 1}`}
          className="w-full h-full object-cover pointer-events-none"
          style={{
            transform: `scale(${pinch.state.scale}) translate(${pinch.state.translateX}px, ${pinch.state.translateY}px) translateX(${swipe.swiping ? swipe.swipeDelta * 0.3 : 0}px)`,
            transition: swipe.swiping ? "none" : "transform 0.3s ease",
            willChange: "transform",
          }}
          onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
        />

        <button
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 border border-[#e8e4dd] flex items-center justify-center shadow-sm hover:bg-white transition-colors"
          aria-label="Open fullscreen viewer"
          onClick={(e) => { e.stopPropagation(); onOpenFullscreen(activeIndex); }}
        >
          <Maximize2 size={13} className="text-[#555]" />
        </button>

        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <div key={i} className={`rounded-full transition-all duration-200 ${
                i === activeIndex ? "w-4 h-1.5 bg-[#1a1a1a]" : "w-1.5 h-1.5 bg-[#1a1a1a]/30"
              }`} />
            ))}
          </div>
        )}
      </div>

      {/* Horizontal thumbnails — tablet (sm..md) */}
      {images.length > 1 && (
        <div className="mt-2 hidden sm:flex md:hidden gap-2 overflow-x-auto hide-scrollbar">
          {images.map((src, i) => (
            <button key={i} onClick={() => { pinch.reset(); onIndexChange(i); }}
              className={`flex-shrink-0 w-[64px] h-[72px] rounded-md border-2 overflow-hidden transition-all duration-200 ${
                i === activeIndex ? "border-[#1a1a1a] scale-105" : "border-[#ddd] hover:border-[#b8955a]"
              }`}>
              <img loading="lazy" src={src} alt={`View ${i + 1}`} className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
});


interface DesktopGalleryProps {
  images:           string[];
  activeIndex:      number;
  onSelect:         (i: number) => void;
  onOpenFullscreen: () => void;
  fade:             boolean;
  productName:      string;
}

const ZOOM_FACTOR = 2.6;

const DesktopGallery = memo(function DesktopGallery({
  images, activeIndex, onSelect, onOpenFullscreen, fade, productName,
}: DesktopGalleryProps) {
  const { zoom, onMouseMove, onMouseLeave } = useZoom();

  const transformOrigin = useMemo(
    () => `${zoom.x * 100}% ${zoom.y * 100}%`,
    [zoom.x, zoom.y]
  );

  return (
    <div className="flex gap-3 h-full group/gallery" style={{ minHeight: 0 }}>

      {/* ── LEFT: vertical thumbnail column ── */}
      {images.length > 1 && (
        <div
          className="flex-shrink-0 hide-scrollbar"
          style={{ width: 76, overflowY: "auto", maxHeight: "100%" }}
        >
          <VerticalThumbnails
            images={images}
            activeIndex={activeIndex}
            onSelect={onSelect}
          />
        </div>
      )}

      {/* ── RIGHT: main large image ── */}
      <div className="flex-1 relative bg-[#f7f4ef] rounded-xl overflow-hidden border border-[#ece7df]">
        {/* Hover-to-zoom image */}
        <div
          className="w-full h-full overflow-hidden"
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onClick={onOpenFullscreen}
          style={{ cursor: zoom.active ? "crosshair" : "zoom-in" }}
          role="button"
          tabIndex={0}
          aria-label="Click to open fullscreen, hover to zoom"
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpenFullscreen(); }}
        >
          <img
            src={images[activeIndex]}
            alt={`${productName} — view ${activeIndex + 1}`}
            draggable={false}
            onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
            className="w-full h-full object-cover pointer-events-none select-none"
            style={{
              opacity: fade ? 1 : 0,
              transform: zoom.active ? `scale(${ZOOM_FACTOR})` : "scale(1)",
              transformOrigin,
              transition: zoom.active
                ? "opacity 0.12s ease, transform 0.06s linear"
                : "opacity 0.16s ease, transform 0.22s cubic-bezier(0.34,1.1,0.64,1)",
              willChange: "transform",
            }}
          />
        </div>

        {/* Expand button */}
        <button
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/85 border border-[#e8e4dd] flex items-center justify-center shadow-sm hover:bg-white transition-colors opacity-0 group-hover/gallery:opacity-100"
          aria-label="Open fullscreen"
          onClick={(e) => { e.stopPropagation(); onOpenFullscreen(); }}
        >
          <Maximize2 size={13} className="text-[#555]" />
        </button>

        {/* Prev / Next arrows when multiple images */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onSelect(activeIndex === 0 ? images.length - 1 : activeIndex - 1); }}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 border border-[#e8e4dd] text-[#555] flex items-center justify-center shadow-sm hover:bg-white transition-colors opacity-0 group-hover/gallery:opacity-100"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onSelect((activeIndex + 1) % images.length); }}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 border border-[#e8e4dd] text-[#555] flex items-center justify-center shadow-sm hover:bg-white transition-colors opacity-0 group-hover/gallery:opacity-100"
            >
              <ChevronRight size={15} />
            </button>
          </>
        )}

        {/* Image counter badge */}
        {images.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/40 text-white font-['DM_Mono'] text-[10px] tracking-[0.15em] px-2 py-0.5 rounded-full pointer-events-none">
            {activeIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
});

// ══════════════════════════════════════════════════════════════════════════════
// ProductGallery – main export
// ══════════════════════════════════════════════════════════════════════════════
export interface ProductGalleryProps {
  images:      string[];
  productName: string;
}

export default function ProductGallery({ images, productName }: ProductGalleryProps) {
  const safeImages = useMemo(
    () => (images.length ? images : [FALLBACK]),
    [images]
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [fade,        setFade]        = useState(true);
  const [fsOpen,      setFsOpen]      = useState(false);
  const [fsStartIdx,  setFsStartIdx]  = useState(0);


  const lastSyncedImages = useRef(safeImages);
  useEffect(() => {
    if (lastSyncedImages.current !== safeImages) {
      lastSyncedImages.current = safeImages;
      setFade(false);
      setActiveIndex(0);
      const t = setTimeout(() => setFade(true), 20);
      return () => clearTimeout(t);
    }
  }, [safeImages]);


  const safeActiveIndex = activeIndex < safeImages.length ? activeIndex : 0;

  const handleSelect = useCallback((i: number) => {

    setFade(false);
    setTimeout(() => { setActiveIndex(i); setFade(true); }, 100);
  }, []);

  const openFullscreen = useCallback((i: number) => {
    setFsStartIdx(i); setFsOpen(true);
  }, []);

  // Preload neighbours
  useEffect(() => {
    const preload = (idx: number) => { const img = new Image(); img.src = safeImages[(idx + safeImages.length) % safeImages.length]; };
    preload(safeActiveIndex + 1); preload(safeActiveIndex - 1);
  }, [safeActiveIndex, safeImages]);

  return (
    <>
      {/* ── Desktop (md+): thumbnails LEFT, big image RIGHT ── */}
      <div className="hidden md:flex h-full" style={{ minHeight: 420 }}>
        <DesktopGallery
          images={safeImages}
          activeIndex={safeActiveIndex}
          onSelect={handleSelect}
          onOpenFullscreen={() => openFullscreen(safeActiveIndex)}
          fade={fade}
          productName={productName}
        />
      </div>

      {/* ── Mobile (<md): swipeable card ── */}
      <div className="md:hidden">
        <MobileGallery
          images={safeImages}
          activeIndex={safeActiveIndex}
          onIndexChange={setActiveIndex}
          onOpenFullscreen={openFullscreen}
          productName={productName}
        />
      </div>

      {/* ── Fullscreen overlay ── */}
      {fsOpen && (
        <FullscreenViewer
          images={safeImages}
          startIndex={fsStartIdx}
          productName={productName}
          onClose={() => setFsOpen(false)}
        />
      )}
    </>
  );
}